/**
 * PNG Export Utility
 *
 * Handles rendering diagrams to PNG data URLs (base64).
 * Can be called from the bridge API to export diagrams programmatically.
 */

import type { ExportDiagramSnapshot } from './exportBounds';

export interface PngExportResult {
  pngData: string; // base64-encoded PNG
  fileName: string;
  pageCount?: number;
}

export interface PngExporterFunctions {
  renderSnapshotToPng: (snapshot: ExportDiagramSnapshot) => Promise<string>;
  buildFileName: (pageName: string, includePageName: boolean) => string;
  getProjectName: () => string;
  getPages: () => Array<{ id: string; name: string }>;
  buildExportSnapshotFromPage: (page: any) => ExportDiagramSnapshot;
  buildCurrentExportSnapshot: (context: 'currentView' | 'rootPage') => ExportDiagramSnapshot;
}

// Global reference to export functions (set by App.tsx)
let exportFunctions: PngExporterFunctions | null = null;

export function registerExportFunctions(fns: PngExporterFunctions) {
  exportFunctions = fns;
}

/**
 * Export current diagram page to PNG
 * @returns Base64-encoded PNG data and suggested filename
 */
export async function exportCurrentPageToPng(): Promise<PngExportResult> {
  if (!exportFunctions) {
    throw new Error('Export functions not initialized');
  }

  const snapshot = exportFunctions.buildCurrentExportSnapshot('currentView');
  const pngData = await exportFunctions.renderSnapshotToPng(snapshot);
  const pages = exportFunctions.getPages();
  const fileName = exportFunctions.buildFileName(snapshot.name, pages.length > 1);

  return { pngData, fileName };
}

/**
 * Export all diagram pages to PNG (one per page)
 * @returns Array of PNG exports with filenames
 */
export async function exportAllPagesToPng(): Promise<PngExportResult[]> {
  if (!exportFunctions) {
    throw new Error('Export functions not initialized');
  }

  const pages = exportFunctions.getPages();
  const results: PngExportResult[] = [];

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const snapshot = exportFunctions.buildExportSnapshotFromPage(page);
    const pngData = await exportFunctions.renderSnapshotToPng(snapshot);
    const fileName = exportFunctions.buildFileName(snapshot.name, true);

    results.push({ pngData, fileName, pageCount: pages.length });

    // Add delay between exports to prevent browser throttling
    if (i < pages.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
  }

  return results;
}

/**
 * Export specific pages to PNG
 * @param pageIds - IDs of pages to export
 */
export async function exportPagesToPng(pageIds: string[]): Promise<PngExportResult[]> {
  if (!exportFunctions) {
    throw new Error('Export functions not initialized');
  }

  const pages = exportFunctions.getPages();
  const results: PngExportResult[] = [];

  for (let i = 0; i < pageIds.length; i++) {
    const pageId = pageIds[i];
    const page = pages.find((p) => p.id === pageId);
    if (!page) continue;

    const snapshot = exportFunctions.buildExportSnapshotFromPage(page);
    const pngData = await exportFunctions.renderSnapshotToPng(snapshot);
    const fileName = exportFunctions.buildFileName(snapshot.name, pageIds.length > 1);

    results.push({ pngData, fileName, pageCount: pageIds.length });

    if (i < pageIds.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
  }

  return results;
}
